/**
 * Landing page — implemented from the Claude Design project
 * "OnFlow Landing.dc.html" (project 27382074, imported 2026-08-16).
 *
 * Three proof sections walk the product loop: the survey → the arithmetic,
 * the TrackRing verdict, and the Sunday review. An ambient canvas repeats the
 * product's own vocabulary (goal arcs, projection wedge, consistency bars)
 * behind everything. All motion freezes under prefers-reduced-motion.
 */
import type { MeResponse } from '@onflow/shared';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ConsistencyLegend, ConsistencyStrip } from '../components/v2/ConsistencyStrip.js';
import type { ConsistencyTone } from '../components/v2/ConsistencyStrip.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard, FlowLabel, FlowMetric } from '../components/v2/FlowCard.js';
import { FlowStatus } from '../components/v2/FlowStatus.js';
import type { TrackState } from '../components/v2/FlowStatus.js';
import { GoalInput } from '../components/v2/GoalInput.js';
import { GoalStrip } from '../components/v2/GoalStrip.js';
import { GoogleMark } from '../components/v2/GoogleMark.js';
import { Icon } from '../components/v2/Icon.js';
import { MacroGroup } from '../components/v2/MacroRow.js';
import { PhoneFrame } from '../components/v2/PhoneFrame.js';
import { ProjectionChart } from '../components/v2/ProjectionChart.js';
import { TrackRing, TrackRingLegend } from '../components/v2/TrackRing.js';
import { Wordmark } from '../components/v2/Wordmark.js';
import { api, apiBaseUrl } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useAuthStore } from '../stores/authStore.js';

const GOAL_INTENT_KEY = 'onflow:landing_goal_intent';
const GOOGLE_OAUTH_URL = `${apiBaseUrl}/auth/google`;

/** Demo pacing multiplier (the design project's demoSpeed prop). */
const SP = 1;
type ScenarioKey = 'off-track' | 'drifting' | 'on-track';

/** Which scenario the review section settles on (the design's scenario prop). */
const SCENARIO_KEY: ScenarioKey = 'off-track';

interface Scenario {
  ringSentence: string;
  statusLabel: string;
  verdictPre: string;
  verdictClause: string;
  verdictPost: string;
  projNote: string;
  changeTitle: string;
  changeFrom: string;
  changeTo: string;
  changeReason: string;
  ring: { progress: number; elapsed: number; value: string; suffix: string };
  tiles: { label: string; value: string; suffix: string; note: string; ink: string }[];
}

const SCENARIOS: Record<ScenarioKey, Scenario> = {
  'off-track': {
    ringSentence: 'Progress 26%, window 42% gone. At this rate the date is missed.',
    statusLabel: 'Off track',
    verdictPre: 'Training held: 4 of 4 sessions. ',
    verdictClause: 'Eating didn’t.',
    verdictPost:
      ' You averaged 406 kcal under target, so four weeks of work moved the scale 1.0 kg instead of 1.5.',
    projNote:
      'You are 1.1 kg behind the line. Hold this pace and you reach 78 kg around Oct 30, 36 days after your date.',
    changeTitle: 'Raise your daily calorie target by 100.',
    changeFrom: '2,547 kcal',
    changeTo: '2,647 kcal',
    changeReason: 'You were consistently under, not enough fuel to grow.',
    ring: { progress: 26, elapsed: 42, value: '26%', suffix: '42 of 73 days left' },
    tiles: [
      {
        label: 'Avg intake',
        value: '2,141',
        suffix: 'kcal',
        note: 'target 2,547',
        ink: 'var(--off-track)',
      },
      {
        label: 'Days logged',
        value: '11',
        suffix: 'of 14',
        note: '3 days counted zero',
        ink: 'var(--drifting)',
      },
      {
        label: 'Protein hit',
        value: '5',
        suffix: 'of 7',
        note: 'good enough',
        ink: 'var(--on-track)',
      },
      {
        label: 'Sessions',
        value: '4',
        suffix: 'of 4',
        note: 'full compliance',
        ink: 'var(--on-track)',
      },
    ],
  },
  drifting: {
    ringSentence: 'Progress 34%, window 42% gone. Slipping, still recoverable.',
    statusLabel: 'Drifting',
    verdictPre: 'Training held. ',
    verdictClause: 'Two days slipped.',
    verdictPost:
      ' You averaged 188 kcal under target, so the gain came in at 1.3 kg where the plan needed 1.5.',
    projNote:
      'You are 0.4 kg behind the line. Hold this pace and you reach 78 kg around Sep 24, 12 days after your date.',
    changeTitle: 'Move 150 kcal from snacks into lunch.',
    changeFrom: '2,547 kcal',
    changeTo: '2,547 kcal',
    changeReason: 'Same daily total, fewer missed evenings. The leak is timing, not the number.',
    ring: { progress: 34, elapsed: 42, value: '34%', suffix: '42 of 73 days left' },
    tiles: [
      {
        label: 'Avg intake',
        value: '2,359',
        suffix: 'kcal',
        note: 'target 2,547',
        ink: 'var(--drifting)',
      },
      {
        label: 'Days logged',
        value: '13',
        suffix: 'of 14',
        note: '1 day counted zero',
        ink: 'var(--on-track)',
      },
      { label: 'Protein hit', value: '6', suffix: 'of 7', note: 'holding', ink: 'var(--on-track)' },
      {
        label: 'Sessions',
        value: '4',
        suffix: 'of 4',
        note: 'full compliance',
        ink: 'var(--on-track)',
      },
    ],
  },
  'on-track': {
    ringSentence: 'Progress 44%, window 42% gone. Pace is holding.',
    statusLabel: 'On track',
    verdictPre: 'Training held and ',
    verdictClause: 'eating held with it.',
    verdictPost:
      ' You averaged 32 kcal off target across seven logged days, and the scale moved 1.6 kg in four weeks.',
    projNote:
      'You are 0.1 kg ahead of the line. Hold this pace and you reach 78 kg around Sep 9, three days early.',
    changeTitle: 'Add one set to your compound lifts.',
    changeFrom: '3 sets',
    changeTo: '4 sets',
    changeReason: 'Two perfect weeks in a row. Time to progress.',
    ring: { progress: 44, elapsed: 42, value: '44%', suffix: '42 of 73 days left' },
    tiles: [
      {
        label: 'Avg intake',
        value: '2,515',
        suffix: 'kcal',
        note: 'target 2,547',
        ink: 'var(--on-track)',
      },
      {
        label: 'Days logged',
        value: '14',
        suffix: 'of 14',
        note: 'nothing missing',
        ink: 'var(--on-track)',
      },
      {
        label: 'Protein hit',
        value: '7',
        suffix: 'of 7',
        note: 'every day',
        ink: 'var(--on-track)',
      },
      {
        label: 'Sessions',
        value: '4',
        suffix: 'of 4',
        note: 'full compliance',
        ink: 'var(--on-track)',
      },
    ],
  },
};

const HERO_LOGS = [
  {
    title: 'Breakfast logged',
    meta: 'Greek yoghurt · oats · banana',
    delta: '+451',
    kcal: 451,
    p: 29,
    c: 58,
    f: 14,
  },
  {
    title: 'Lunch logged',
    meta: 'Chicken breast 180 g · brown rice',
    delta: '+562',
    kcal: 1013,
    p: 76,
    c: 121,
    f: 33,
  },
  {
    title: 'Snack logged',
    meta: 'Paneer bhurji · 2 roti',
    delta: '+398',
    kcal: 1411,
    p: 108,
    c: 168,
    f: 47,
  },
  {
    title: 'Dinner logged',
    meta: 'Toor dal · rice · curd',
    delta: '+429',
    kcal: 1840,
    p: 131,
    c: 214,
    f: 58,
  },
];

const STRIP: [string, number, ConsistencyTone][] = [
  ['19', 78, 'under'],
  ['20', 96, 'hit'],
  ['21', 68, 'under'],
  ['22', 0, 'none'],
  ['23', 88, 'hit'],
  ['24', 72, 'under'],
  ['25', 0, 'none'],
  ['26', 84, 'hit'],
  ['27', 63, 'under'],
  ['28', 91, 'hit'],
  ['29', 0, 'none'],
  ['30', 70, 'under'],
  ['31', 86, 'hit'],
  ['1', 52, 'under'],
];

const SURVEY = [
  { num: '01', label: 'Mission', answer: 'Gain muscle', pill: 'Locked', tone: 'ember' },
  {
    num: '02',
    label: 'Target + date',
    answer: '74.2 → 78.0 kg by Sep 12',
    pill: 'Safe rate',
    tone: 'on-track',
  },
  {
    num: '03',
    label: 'Your numbers',
    answer: '27 · male · 178 cm · eggetarian',
    pill: 'Private',
    tone: 'dim',
  },
  {
    num: '04',
    label: 'A normal week',
    answer: 'Desk job · football 2–3× a week',
    pill: '1.34×',
    tone: 'ember',
  },
  {
    num: '05',
    label: 'Training',
    answer: 'Gym · 4 days · 45–60 min · intermediate',
    pill: 'Upper / lower',
    tone: 'dim',
  },
] as const;

const PILLS: Record<string, [string, string, string]> = {
  ember: ['var(--ember-wash)', 'var(--ember)', 'rgba(238,107,51,0.32)'],
  'on-track': ['var(--on-track-wash)', 'var(--on-track)', 'rgba(69,212,131,0.32)'],
  dim: ['var(--paper-3)', 'var(--ink-3)', 'var(--edge)'],
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const easeOut = (n: number) => 1 - Math.pow(1 - clamp01(n), 3);
const comma = (n: number) => Math.round(n).toLocaleString('en-US');

const monoLabel: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

export function Login() {
  const status = useAuthStore((s) => s.status);
  const setSession = useAuthStore((s) => s.setSession);
  const setIdentity = useAuthStore((s) => s.setIdentity);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const s1Ref = useRef<HTMLElement>(null);
  const s3Ref = useRef<HTMLElement>(null);
  const s1Skip = useRef(false);
  const s3Skip = useRef(false);

  const [t, setT] = useState(0);
  const [s1At, setS1At] = useState<number | null>(null);
  const [s3At, setS3At] = useState<number | null>(null);

  const reduced = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );

  // Demo clock + scroll-reveal observers.
  useEffect(() => {
    const t0 = performance.now();
    const tick = window.setInterval(() => setT(performance.now() - t0), 66);

    // Anything already on screen at load renders finished — only sections the
    // user scrolls down to get the reveal, so nothing is ever blank on arrival.
    const onScreen = (el: Element | null) =>
      !!el && el.getBoundingClientRect().top < window.innerHeight * 0.85;
    s1Skip.current = onScreen(s1Ref.current);
    s3Skip.current = onScreen(s3Ref.current);

    let io: IntersectionObserver | undefined;
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            const now = performance.now() - t0;
            if (en.target === s1Ref.current) setS1At((v) => v ?? now);
            if (en.target === s3Ref.current) setS3At((v) => v ?? now);
          });
        },
        // Fires just before the section is on screen, so the reveal starts
        // from zero off-stage and the user only ever sees it counting up.
        { threshold: 0.01, rootMargin: '0px 0px 22% 0px' },
      );
      if (s1Ref.current) io.observe(s1Ref.current);
      if (s3Ref.current) io.observe(s3Ref.current);
    } else {
      s1Skip.current = true;
      s3Skip.current = true;
    }

    return () => {
      window.clearInterval(tick);
      io?.disconnect();
    };
  }, []);

  /* Ambient layer, drawn from the product's own vocabulary: two goal arcs
     whose dim companion keeps overtaking them, a projection wedge, and a bar
     field that behaves like a consistency strip. No decorative gradients. */
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    const fit = () => {
      const d = Math.min(2, window.devicePixelRatio || 1);
      w = window.innerWidth;
      h = window.innerHeight;
      cv.width = Math.round(w * d);
      cv.height = Math.round(h * d);
      ctx.setTransform(d, 0, 0, d, 0, 0);
    };
    fit();
    window.addEventListener('resize', fit);

    const speed = 0.00016 * SP;
    const frozen = reduced;
    const started = performance.now();

    const arc = (
      cx: number,
      cy: number,
      r: number,
      from: number,
      to: number,
      color: string,
      width: number,
      alpha: number,
    ) => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, from, to);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.globalAlpha = alpha;
      ctx.lineCap = 'round';
      ctx.stroke();
    };

    const draw = () => {
      // A frozen composition still paints one frame — the background is never bare.
      const tt = (frozen ? 4200 : performance.now() - started) * speed;
      ctx.clearRect(0, 0, w, h);

      // 1 — the goal ring, oversized, off-canvas centre
      const cx = w * 0.86;
      const cy = h * 0.2;
      const r = Math.max(w, h) * 0.52;
      const sweep = 0.52 + Math.sin(tt * 0.7) * 0.16;
      const elapsed = 0.62 + Math.sin(tt * 0.7 + 1.1) * 0.16;
      const behind = elapsed > sweep;
      arc(
        cx,
        cy,
        r,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * sweep,
        behind ? '#F5C24B' : '#45D483',
        2.5,
        0.085,
      );
      arc(
        cx,
        cy,
        r - 26,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * elapsed,
        '#948E85',
        1.5,
        0.075,
      );
      arc(cx, cy, r, 0, Math.PI * 2, '#F7F3EA', 1, 0.022);
      arc(cx, cy, r - 26, 0, Math.PI * 2, '#F7F3EA', 1, 0.016);

      // 2 — second ring, lower left, out of phase
      const cx2 = w * 0.06;
      const cy2 = h * 0.92;
      const r2 = Math.max(w, h) * 0.34;
      const sweep2 = 0.42 + Math.sin(tt * 0.55 + 2.4) * 0.2;
      arc(cx2, cy2, r2, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * sweep2, '#EE6B33', 2, 0.07);
      arc(cx2, cy2, r2 - 20, 0, Math.PI * 2, '#F7F3EA', 1, 0.016);

      // 3 — projection wedge: required line vs the trend that keeps falling short
      const y0 = h * 0.68;
      const span = w;
      ctx.globalAlpha = 0.06;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 7]);
      ctx.strokeStyle = '#948E85';
      ctx.beginPath();
      for (let x = 0; x <= span; x += 26) ctx.lineTo(x, y0 - (x / span) * h * 0.3);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = '#EE6B33';
      ctx.globalAlpha = 0.075;
      ctx.beginPath();
      for (let x = 0; x <= span; x += 18) {
        const prog = x / span;
        ctx.lineTo(x, y0 - prog * h * 0.3 * (0.52 + Math.sin(tt * 0.9 + prog * 3.4) * 0.12));
      }
      ctx.stroke();

      // 4 — consistency bar field along the floor
      const bw = 10;
      const gapw = 16;
      const n = Math.ceil(w / (bw + gapw));
      for (let i = 0; i < n; i++) {
        const phase = Math.sin(tt * 1.5 + i * 0.42);
        const bh = 26 + phase * 22 + 26;
        const scan = ((tt * 40) % (n + 14)) - i;
        const lit = scan > 0 && scan < 1.6;
        ctx.globalAlpha = lit ? 0.3 : 0.055;
        ctx.fillStyle = lit
          ? '#EE6B33'
          : phase > 0.55
            ? '#45D483'
            : phase < -0.62
              ? '#FF5A6E'
              : '#948E85';
        const x = i * (bw + gapw);
        ctx.beginPath();
        ctx.roundRect(x, h - bh - 8, bw, bh, 5);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    draw();
    let paint: number | undefined;
    if (!frozen) paint = window.setInterval(draw, 33);

    return () => {
      if (paint !== undefined) window.clearInterval(paint);
      window.removeEventListener('resize', fit);
    };
  }, [reduced]);

  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;

  const showTestSignIn =
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('test-auth') === '1';

  function handleGoalSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = goal.trim();
    if (trimmed.length > 0) {
      try {
        localStorage.setItem(GOAL_INTENT_KEY, trimmed);
      } catch {
        // Silently ignore quota/private-mode failures — goal is a hint, not a contract.
      }
    }
    window.location.href = GOOGLE_OAUTH_URL;
  }

  async function handleTestSignIn() {
    setBusy(true);
    try {
      const res = await api<{ accessToken: string; accessTokenExpiresAt: string }>(
        '/auth/test-session',
        { method: 'POST', auth: false, json: {} },
      );
      setSession(res.accessToken, res.accessTokenExpiresAt);
      const me = await api<MeResponse>('/auth/me');
      setIdentity(me);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sign-in failed');
      setBusy(false);
    }
  }

  /* ────────────────────── derived demo values ────────────────────── */
  const sc = SCENARIOS[SCENARIO_KEY];
  // No clock (yet, or by preference) means every value renders at its finished
  // state: the page is complete and legible before a single frame has run.
  const still = reduced || t === 0;

  /* ── hero: food logging loop ── */
  const stepMs = 3000 / SP;
  const idx = still ? 3 : Math.floor((t % (stepMs * 4)) / stepMs);
  const log = HERO_LOGS[idx]!;
  const within = t % stepMs;
  const toastIn = clamp01(within / (220 / SP));
  const toastOut = 1 - clamp01((within - stepMs * 0.76) / (280 / SP));
  const toastA = still ? 1 : Math.min(toastIn, toastOut);
  const heroPct = (log.kcal / 2547) * 100;
  const heroState: Exclude<TrackState, 'neutral'> =
    heroPct >= 57 ? 'on-track' : heroPct >= 47 ? 'drifting' : 'off-track';

  /* ── section 1: survey rows, then the arithmetic ── */
  const p1 = still || s1Skip.current || s1At === null ? 1 : clamp01(((t - s1At) / 2600) * SP);
  const surveyRows = SURVEY.map((r, i) => {
    const a = easeOut((p1 - i * 0.09) / 0.3);
    const [pillBg, pillInk, pillEdge] = PILLS[r.tone]!;
    return { ...r, pillBg, pillInk, pillEdge, opacity: a, shift: 14 - 14 * a };
  });
  const n1 = easeOut((p1 - 0.46) / 0.54);
  const mathRows = [
    { name: 'Basal rate', note: 'Mifflin-St Jeor', value: comma(1715 * n1), ink: 'var(--ink-1)' },
    {
      name: 'Activity multiplier',
      note: 'MET table + lifestyle',
      value: '×' + (1.34 * n1).toFixed(2),
      ink: 'var(--ember)',
    },
    { name: 'Maintenance', note: 'TDEE', value: comma(2297 * n1), ink: 'var(--ink-1)' },
    {
      name: 'Muscle-gain surplus',
      note: 'goal: gain muscle',
      value: '+' + comma(250 * n1),
      ink: 'var(--on-track)',
    },
  ];

  /* ── section 2: the ring cycles through the triad, settling on the scenario ── */
  const phases = [
    {
      progress: 44,
      elapsed: 42,
      value: '44%',
      sentence: SCENARIOS['on-track'].ringSentence,
      label: SCENARIOS['on-track'].statusLabel,
      ink: 'var(--on-track)',
    },
    {
      progress: 34,
      elapsed: 42,
      value: '34%',
      sentence: SCENARIOS.drifting.ringSentence,
      label: SCENARIOS.drifting.statusLabel,
      ink: 'var(--drifting)',
    },
    {
      progress: 26,
      elapsed: 42,
      value: '26%',
      sentence: SCENARIOS['off-track'].ringSentence,
      label: SCENARIOS['off-track'].statusLabel,
      ink: 'var(--off-track)',
    },
  ];
  const ph = still ? phases[2]! : phases[Math.floor((t % (10800 / SP)) / (3600 / SP))]!;
  const ringState: Exclude<TrackState, 'neutral'> =
    ph.progress >= ph.elapsed - 5
      ? 'on-track'
      : ph.progress >= ph.elapsed - 15
        ? 'drifting'
        : 'off-track';
  const stripDays = STRIP.map(([label, value, tone], i) => ({
    label,
    value,
    tone,
    today: i === STRIP.length - 1,
  }));

  /* ── section 3: verdict types out, then three stages ── */
  const settled = still || s3Skip.current || s3At === null;
  const full = sc.verdictPre + sc.verdictClause + sc.verdictPost;
  const chars = settled ? full.length : s3At === null ? 0 : Math.floor(((t - s3At) / 17) * SP);
  const cut = (from: number, len: number) =>
    full.slice(from, Math.max(from, Math.min(from + len, chars)));
  const preLen = sc.verdictPre.length;
  const clLen = sc.verdictClause.length;
  const done = chars >= full.length;
  const after = s3At === null ? -1 : (t - s3At) * SP - full.length * 17;
  const stage = (i: number) => (settled ? 1 : easeOut((after - i * 420) / 520));
  const verdictInk = (
    {
      'on-track': 'var(--on-track)',
      drifting: 'var(--drifting)',
      'off-track': 'var(--off-track)',
    } satisfies Record<ScenarioKey, string>
  )[SCENARIO_KEY];
  const stageStyle = (i: number): CSSProperties => ({
    opacity: stage(i).toFixed(3),
    transform: `translateY(${(16 - 16 * stage(i)).toFixed(1)}px)`,
    transition: 'opacity 520ms var(--ease-out), transform 520ms var(--ease-out)',
  });

  const projPoints = [{ v: 74.2 }, { v: 74.4 }, { v: 74.7 }, { v: 75.0 }, { v: 75.2 }];
  const projRequired = [
    { v: 74.2 },
    { v: 74.7 },
    { v: 75.2 },
    { v: 75.7 },
    { v: 76.2 },
    { v: 76.7 },
    { v: 77.3 },
    { v: 78 },
  ];

  return (
    <div className="landing-v2">
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          background:
            'radial-gradient(115% 80% at 78% 8%, rgba(36,32,27,0.55) 0%, rgba(11,10,9,0) 62%)',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <nav
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            background: 'rgba(11,10,9,0.82)',
            backdropFilter: 'saturate(140%) blur(14px)',
            borderBottom: '1px solid var(--edge)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              padding: '15px 32px',
              maxWidth: 1200,
              margin: '0 auto',
            }}
          >
            <Wordmark size={22} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
              <a href={GOOGLE_OAUTH_URL} style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                Sign in
              </a>
            </div>
          </div>
        </nav>

        {/* ───────── HERO ───────── */}
        <header className="landing-hero">
          <div style={{ minWidth: 0 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                ...monoLabel,
                fontSize: 11,
                marginBottom: 20,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--ember)',
                  boxShadow: '0 0 12px rgba(238,107,51,0.7)',
                }}
              />
              Goal-locked tracking
            </span>
            <h1
              style={{
                fontFamily: 'var(--font-brand), Georgia, serif',
                fontWeight: 380,
                fontSize: 'clamp(44px, 5.6vw, 84px)',
                lineHeight: 0.98,
                letterSpacing: '-0.025em',
                margin: '0 0 22px',
                fontVariationSettings: "'opsz' 144",
              }}
            >
              Set your targets.
              <br />
              <em style={{ fontStyle: 'italic', fontWeight: 380 }}>Track the work.</em>
            </h1>
            <p
              style={{
                margin: '0 0 30px',
                color: 'var(--ink-2)',
                fontSize: 17,
                lineHeight: 1.55,
                maxWidth: 520,
                textWrap: 'pretty',
              }}
            >
              Name a goal, date it, answer four screens. OnFlow does the arithmetic, then tells you
              every Sunday whether you are still going to hit it, and the one thing to change if you
              are not.
            </p>

            <div id="start" style={{ maxWidth: 560 }}>
              <GoalInput
                placeholder="gain 3.8kg by Sep 12"
                submitLabel="Start"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                onSubmit={handleGoalSubmit}
                style={{ maxWidth: 560 }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  margin: '22px 0 16px',
                  ...monoLabel,
                  letterSpacing: '0.16em',
                  fontSize: 10,
                }}
              >
                <span style={{ flex: 1, height: 1, background: 'var(--edge)' }} />
                or
                <span style={{ flex: 1, height: 1, background: 'var(--edge)' }} />
              </div>
              <a href={GOOGLE_OAUTH_URL} className="landing-google-cta">
                <GoogleMark size={18} />
                Continue with Google
              </a>
              {showTestSignIn && (
                <button
                  type="button"
                  className="landing-dev-test"
                  onClick={handleTestSignIn}
                  disabled={busy}
                >
                  {busy ? 'Signing in…' : 'Continue as test user (dev)'}
                </button>
              )}
            </div>
          </div>

          {/* Hero device: food logging, live */}
          <div className="landing-hero-device">
            <div
              style={{
                transform: 'scale(0.62)',
                transformOrigin: 'top center',
                height: 560,
                position: 'relative',
              }}
            >
              <PhoneFrame time="20:41">
                <div
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    padding: '6px 18px 0',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{ display: 'block', ...monoLabel, fontSize: 11, letterSpacing: '0.1em' }}
                  >
                    Saturday, 1 August
                  </span>
                  <p
                    style={{
                      margin: '6px 0 14px',
                      fontFamily: 'var(--font-display)',
                      fontSize: 26,
                      fontWeight: 700,
                      letterSpacing: '-0.03em',
                    }}
                  >
                    Morning, James
                  </p>

                  <GoalStrip
                    goal="Gain 3.8 kg"
                    deadline="Jul 1 → Sep 12"
                    daysLeft={42}
                    state="off-track"
                    progress={26}
                    compact
                    style={{ marginBottom: 12 }}
                  />

                  <FlowCard padding="lg" radius="lg" status={heroState} glow>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 14,
                      }}
                    >
                      <TrackRing
                        progress={Math.round(heroPct * 10) / 10}
                        elapsed={62}
                        size={176}
                        label="Eaten today"
                        value={comma(log.kcal)}
                        suffix="of 2,547 kcal"
                      />
                      <TrackRingLegend
                        state={heroState}
                        progressLabel="Today's intake"
                        elapsedLabel="Day elapsed"
                      />
                      <div style={{ width: '100%', height: 1, background: 'var(--edge)' }} />
                      <MacroGroup
                        protein={{ current: log.p, target: 148 }}
                        carbs={{ current: log.c, target: 330 }}
                        fat={{ current: log.f, target: 71 }}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </FlowCard>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <FlowCard padding="sm">
                      <FlowLabel>Left today</FlowLabel>
                      <FlowMetric
                        value={comma(2547 - log.kcal)}
                        suffix="kcal"
                        size="sm"
                        style={{ marginTop: 8 }}
                      />
                    </FlowCard>
                    <FlowCard padding="sm">
                      <FlowLabel>Body weight</FlowLabel>
                      <FlowMetric value="75.2" suffix="kg" size="sm" style={{ marginTop: 8 }} />
                    </FlowCard>
                  </div>

                  {/* the log receipt sliding in */}
                  <div
                    style={{
                      position: 'absolute',
                      left: 18,
                      right: 18,
                      bottom: 26,
                      transition: 'opacity 240ms var(--ease-out), transform 320ms var(--ease-snap)',
                      opacity: toastA.toFixed(3),
                      transform: `translateY(${(16 - 16 * easeOut(toastA)).toFixed(1)}px)`,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '13px 15px',
                        background: 'var(--paper-2)',
                        border: '1px solid var(--edge)',
                        borderRadius: 'var(--r-md)',
                        boxShadow: 'var(--elev-3)',
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 'var(--r-xs)',
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          background: 'var(--on-track-wash)',
                          color: 'var(--on-track)',
                          border: '1px solid rgba(69,212,131,0.35)',
                        }}
                      >
                        <Icon name="check" size={16} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--ink-1)',
                          }}
                        >
                          {log.title}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            color: 'var(--ink-3)',
                            marginTop: 2,
                          }}
                        >
                          {log.meta}
                        </span>
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                          color: 'var(--on-track)',
                          flexShrink: 0,
                        }}
                      >
                        {log.delta} kcal
                      </span>
                    </div>
                  </div>
                </div>
              </PhoneFrame>
            </div>
          </div>
        </header>

        {/* ───────── PROOF 1 · the survey ───────── */}
        <section
          ref={s1Ref}
          style={{ borderTop: '1px solid var(--edge)', background: 'rgba(19,17,16,0.42)' }}
        >
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 32px' }}>
            <span
              style={{
                display: 'block',
                ...monoLabel,
                fontSize: 11,
                color: 'var(--ember)',
                marginBottom: 14,
              }}
            >
              Four screens
            </span>
            <h2 className="landing-h2" style={{ maxWidth: '20ch' }}>
              Then arithmetic, not a guess.
            </h2>
            <p className="landing-lede" style={{ marginBottom: 44 }}>
              Mission and date. Your numbers. How you actually spend a week. What training you can
              commit to. Nothing else, and no target appears until the maths has run.
            </p>

            <div className="landing-survey-grid">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {surveyRows.map((row) => (
                  <div
                    key={row.num}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      padding: '16px 18px',
                      border: '1px solid var(--edge)',
                      borderRadius: 'var(--r-md)',
                      background: 'var(--paper-1)',
                      boxShadow: 'var(--elev-1)',
                      opacity: row.opacity.toFixed(3),
                      transform: `translateY(${row.shift.toFixed(1)}px)`,
                      transition: 'opacity 420ms var(--ease-out), transform 420ms var(--ease-out)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        width: 22,
                        flexShrink: 0,
                        paddingTop: 3,
                      }}
                    >
                      {row.num}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', ...monoLabel, fontSize: 10 }}>
                        {row.label}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 15,
                          fontWeight: 600,
                          color: 'var(--ink-1)',
                          marginTop: 5,
                          lineHeight: 1.4,
                        }}
                      >
                        {row.answer}
                      </span>
                    </span>
                    <span
                      style={{
                        flexShrink: 0,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        padding: '5px 10px',
                        borderRadius: 999,
                        background: row.pillBg,
                        color: row.pillInk,
                        border: `1px solid ${row.pillEdge}`,
                      }}
                    >
                      {row.pill}
                    </span>
                  </div>
                ))}
                <p
                  style={{
                    margin: '12px 0 0',
                    color: 'var(--ink-3)',
                    fontSize: 13,
                    lineHeight: 1.6,
                    textWrap: 'pretty',
                  }}
                >
                  Your activity multiplier is computed, not picked from a dropdown: football at MET
                  7.0, two to three sessions, 75 minutes each, added to a desk-job baseline and
                  capped between 1.2 and 2.5.
                </p>
              </div>

              <FlowCard padding="lg" radius="lg">
                <FlowLabel>The arithmetic</FlowLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginTop: 18 }}>
                  {mathRows.map((m) => (
                    <div
                      key={m.name}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 16,
                        padding: '14px 0',
                        borderBottom: '1px solid var(--edge)',
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 14, color: 'var(--ink-2)' }}>
                          {m.name}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 10,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--ink-3)',
                            marginTop: 4,
                          }}
                        >
                          {m.note}
                        </span>
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 20,
                          fontWeight: 700,
                          letterSpacing: '-0.03em',
                          fontVariantNumeric: 'tabular-nums',
                          color: m.ink,
                          flexShrink: 0,
                        }}
                      >
                        {m.value}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 16,
                    padding: '20px 0 18px',
                  }}
                >
                  <span>
                    <FlowLabel tone="action">Daily target</FlowLabel>
                    <span
                      style={{
                        display: 'block',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'var(--ink-3)',
                        marginTop: 6,
                      }}
                    >
                      Locked for the next 7 days
                    </span>
                  </span>
                  <FlowMetric value={comma(2547 * n1)} suffix="kcal" size="md" />
                </div>
                <div style={{ height: 1, background: 'var(--edge)', marginBottom: 18 }} />
                <MacroGroup
                  protein={{ current: Math.round(148 * n1), target: 148 }}
                  carbs={{ current: Math.round(330 * n1), target: 330 }}
                  fat={{ current: Math.round(71 * n1), target: 71 }}
                />
                <p
                  style={{
                    margin: '18px 0 0',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    lineHeight: 1.7,
                    color: 'var(--ink-3)',
                  }}
                >
                  Protein 2.0 g/kg · fat at 25% of intake · carbs take the remainder.
                </p>
              </FlowCard>
            </div>
          </div>
        </section>

        {/* ───────── PROOF 2 · the ring ───────── */}
        <section style={{ borderTop: '1px solid var(--edge)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 32px' }}>
            <div className="landing-ring-grid">
              <div
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 }}
              >
                <TrackRing
                  progress={ph.progress}
                  elapsed={ph.elapsed}
                  size={300}
                  label="Goal progress"
                  value={ph.value}
                  suffix="42 of 73 days left"
                />
                <FlowStatus state={ringState} label={ph.label} />
              </div>
              <div style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    ...monoLabel,
                    fontSize: 11,
                    color: 'var(--ember)',
                    marginBottom: 14,
                  }}
                >
                  The one idea
                </span>
                <h2 className="landing-h2" style={{ maxWidth: '22ch' }}>
                  The dim arc is your deadline.
                </h2>
                <p
                  style={{
                    margin: '0 0 18px',
                    color: 'var(--ink-2)',
                    fontSize: 16,
                    lineHeight: 1.6,
                    maxWidth: '56ch',
                    textWrap: 'pretty',
                  }}
                >
                  The bright arc is how far you have come. The dim one behind it is how much of your
                  window is gone. When the dim arc pulls ahead, the date is slipping away from you;
                  nobody has to write that down.
                </p>
                <p
                  style={{
                    margin: '0 0 28px',
                    fontFamily: 'var(--font-display)',
                    fontSize: 19,
                    lineHeight: 1.4,
                    letterSpacing: '-0.02em',
                    fontWeight: 650,
                    color: ph.ink,
                    transition: 'color var(--t-state) var(--ease-out)',
                  }}
                >
                  {ph.sentence}
                </p>

                <FlowCard padding="md">
                  <FlowLabel>Last 14 days · intake</FlowLabel>
                  <div style={{ marginTop: 14 }}>
                    <ConsistencyStrip days={stripDays} height={62} />
                  </div>
                  <ConsistencyLegend style={{ marginTop: 16 }} />
                  <p
                    style={{
                      margin: '16px 0 0',
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--ink-2)',
                      textWrap: 'pretty',
                    }}
                  >
                    Averages divide by seven. A day you never logged counts as zero, so three quiet
                    days cannot hide inside a decent weekly number.
                  </p>
                </FlowCard>
              </div>
            </div>
          </div>
        </section>

        {/* ───────── PROOF 3 · Sunday review ───────── */}
        <section
          ref={s3Ref}
          style={{ borderTop: '1px solid var(--edge)', background: 'rgba(19,17,16,0.42)' }}
        >
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '88px 32px' }}>
            <span
              style={{
                display: 'block',
                ...monoLabel,
                fontSize: 11,
                color: 'var(--ember)',
                marginBottom: 14,
              }}
            >
              Sunday, 21:00 IST
            </span>
            <h2 className="landing-h2" style={{ maxWidth: '22ch' }}>
              One verdict. One change.
            </h2>
            <p className="landing-lede" style={{ marginBottom: 40 }}>
              Every week OnFlow reads your logs against your own target, says what happened, then
              proposes exactly one adjustment. Never a list of tips. You apply it or you
              don&rsquo;t.
            </p>

            <div style={{ maxWidth: '74ch', margin: '0 0 40px', minHeight: 132 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: 'clamp(22px, 2.5vw, 31px)',
                  lineHeight: 1.26,
                  letterSpacing: '-0.03em',
                  fontWeight: 650,
                  color: 'var(--ink-1)',
                  textWrap: 'pretty',
                }}
              >
                {cut(0, preLen)}
                <span style={{ color: verdictInk }}>{cut(preLen, clLen)}</span>
                {cut(preLen + clLen, full.length)}
                <span
                  style={{
                    display: 'inline-block',
                    width: 3,
                    height: '0.95em',
                    marginLeft: 3,
                    verticalAlign: '-0.12em',
                    background: 'var(--ember)',
                    opacity: s3At !== null && !done ? 1 : 0,
                    animation: 'onflow-caret 1s steps(1, end) infinite',
                  }}
                />
              </p>
            </div>

            <div className="landing-tiles" style={stageStyle(0)}>
              {sc.tiles.map((w) => (
                <FlowCard key={w.label} padding="sm">
                  <FlowLabel>{w.label}</FlowLabel>
                  <FlowMetric
                    value={w.value}
                    suffix={w.suffix}
                    size="sm"
                    color={w.ink}
                    style={{ marginTop: 8 }}
                  />
                  <span
                    style={{
                      display: 'block',
                      marginTop: 8,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--ink-3)',
                    }}
                  >
                    {w.note}
                  </span>
                </FlowCard>
              ))}
            </div>

            <div className="landing-review-grid">
              <div style={stageStyle(1)}>
                <FlowCard padding="md">
                  <FlowLabel>Where this pace lands you</FlowLabel>
                  <div style={{ marginTop: 16 }}>
                    <ProjectionChart
                      state={ringState}
                      goalValue={78}
                      unit="kg"
                      points={projPoints}
                      required={projRequired}
                      height={180}
                      note={sc.projNote}
                    />
                  </div>
                </FlowCard>
              </div>

              <div style={stageStyle(2)}>
                <FlowCard padding="md" status="action">
                  <FlowLabel tone="action">The one change</FlowLabel>
                  <p
                    style={{
                      margin: '14px 0 0',
                      fontFamily: 'var(--font-display)',
                      fontSize: 19,
                      lineHeight: 1.34,
                      letterSpacing: '-0.02em',
                      fontWeight: 650,
                      color: 'var(--ink-1)',
                    }}
                  >
                    {sc.changeTitle}
                  </p>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 0' }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 13,
                        color: 'var(--ink-3)',
                        textDecoration: 'line-through',
                      }}
                    >
                      {sc.changeFrom}
                    </span>
                    <Icon name="arrowRight" size={15} style={{ color: 'var(--ember)' }} />
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 15,
                        color: 'var(--ink-1)',
                      }}
                    >
                      {sc.changeTo}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: '16px 0 0',
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--ink-2)',
                    }}
                  >
                    {sc.changeReason}
                  </p>
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--ink-3)',
                    }}
                  >
                    Nothing about your training changes.
                  </p>
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <FlowButton variant="quiet" block>
                      Not now
                    </FlowButton>
                    <FlowButton variant="primary" block>
                      Apply change
                    </FlowButton>
                  </div>
                </FlowCard>
              </div>
            </div>
          </div>
        </section>

        {/* ───────── CLOSE ───────── */}
        <section style={{ borderTop: '1px solid var(--edge)' }}>
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              padding: '96px 32px 88px',
              textAlign: 'center',
            }}
          >
            <h2
              style={{
                margin: '0 auto 18px',
                fontFamily: 'var(--font-brand), Georgia, serif',
                fontWeight: 380,
                fontSize: 'clamp(32px, 4.4vw, 58px)',
                lineHeight: 1.02,
                letterSpacing: '-0.025em',
                maxWidth: '24ch',
                fontVariationSettings: "'opsz' 144",
              }}
            >
              A goal without a date{' '}
              <em style={{ fontStyle: 'italic', fontWeight: 380 }}>isn&rsquo;t a goal.</em>
            </h2>
            <p
              style={{
                margin: '0 auto 32px',
                color: 'var(--ink-2)',
                fontSize: 16,
                lineHeight: 1.6,
                maxWidth: '52ch',
                textWrap: 'pretty',
              }}
            >
              Give OnFlow a date and it will hold you to it, honestly, every week, in your own
              numbers.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <a href={GOOGLE_OAUTH_URL} className="landing-ember-cta">
                <GoogleMark size={18} />
                Continue with Google
              </a>
            </div>
          </div>
        </section>

        <footer style={{ borderTop: '1px solid var(--edge)', padding: '22px 0' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: 16,
              maxWidth: 1200,
              margin: '0 auto',
              padding: '0 32px',
              color: 'var(--ink-3)',
              fontSize: 13,
            }}
          >
            <Wordmark size={22} />
            <div style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
              <a href="#privacy" style={{ color: 'var(--ink-3)' }}>
                Privacy
              </a>
              <a href="#terms" style={{ color: 'var(--ink-3)' }}>
                Terms
              </a>
              <a href="mailto:onflowai.fitness@gmail.com" style={{ color: 'var(--ink-3)' }}>
                onflowai.fitness@gmail.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
